import { useEffect } from 'react';
import { useBillingAccess } from '@/hooks/useBillingAccess';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * BillingNotifications - Creates database notifications for billing events
 * This is a non-rendering component that monitors billing status and creates
 * notifications in the database for tenant admins when action is needed.
 */
export const BillingNotifications: React.FC = () => {
  const { graceActive, graceUntil, locked, trialStatus, trialHoursLeft } = useBillingAccess();
  const { isTenantAdmin, tenantId, user } = useAuth();

  useEffect(() => {
    // Debug logging for QA testing
    console.log('[BillingNotifications] useEffect triggered', {
      isTenantAdmin,
      tenantId: tenantId?.slice(0, 8) + '...',
      userId: user?.id?.slice(0, 8) + '...',
      trialStatus,
      trialHoursLeft,
      graceActive,
      locked,
    });

    // Only create notifications for tenant admins
    if (!isTenantAdmin || !tenantId || !user?.id) {
      console.log('[BillingNotifications] Skipping notification creation - missing requirements', {
        isTenantAdmin,
        hasTenantId: !!tenantId,
        hasUserId: !!user?.id,
      });
      return;
    }

    const createBillingNotification = async (
      type: string,
      title: string,
      message: string,
      metadata?: Record<string, any>
    ) => {
      try {
        // Check if we already have a recent notification of this specific billing event
        const { data: existing } = await supabase
          .from('notifications')
          .select('id, created_at, metadata')
          .eq('user_id', user.id)
          .eq('type', type)
          .eq('is_read', false)
          .order('created_at', { ascending: false })
          .limit(5); // Check last 5 to filter by billing event

        // If there's a recent notification of the same billing event (within last 6 hours), don't create another
        if (existing && existing.length > 0) {
          const sixHoursAgo = new Date(Date.now() - 6 * 60 * 60 * 1000);
          const duplicateExists = existing.some(notif => {
            const notifTime = new Date(notif.created_at);
            const sameEvent = notif.metadata?.billingEvent === metadata?.billingEvent;
            return notifTime > sixHoursAgo && sameEvent;
          });

          if (duplicateExists) {
            console.log('[BillingNotifications] Skipping duplicate notification', {
              type,
              billingEvent: metadata?.billingEvent,
              existingCount: existing.length,
            });
            return; // Skip creating duplicate notification
          }
        }

        const { error } = await supabase.from('notifications').insert({
          user_id: user.id,
          tenant_id: tenantId,
          type,
          title,
          message,
          metadata: metadata || {},
          is_read: false,
        });

        if (error) {
          console.error('Error creating billing notification:', error);
        } else {
          console.log('[BillingNotifications] Successfully created notification', {
            type,
            title,
            billingEvent: metadata?.billingEvent,
          });

          // Trigger a manual refresh of notifications to ensure they appear immediately
          // This helps with real-time subscription delays
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('refreshNotifications'));
          }, 100);
        }
      } catch (err) {
        console.error('Failed to create billing notification:', err);
      }
    };

    // Critical billing issues - always notify
    if (locked) {
      createBillingNotification(
        'billing_payment_failed',
        'Account Access Restricted',
        'Your account access has been restricted due to a billing issue. Please update your payment method immediately.',
        { severity: 'critical', graceUntil, billingEvent: 'locked' }
      );
    } else if (graceActive) {
      createBillingNotification(
        'billing_payment_failed',
        'Payment Issue Detected',
        `We were unable to process your last payment. Your account is in a grace period until ${graceUntil ? new Date(graceUntil).toLocaleDateString() : 'soon'}. Please update your card.`,
        { severity: 'high', graceUntil, billingEvent: 'grace_period' }
      );
    }
    // Trial notifications - use proper trial notification types
    else if (trialStatus === 'expired') {
      createBillingNotification(
        'billing_trial_ended',
        'Trial Period Ended',
        'Your trial has ended and billing has begun automatically using your saved payment method. You can review your billing details anytime.',
        { severity: 'info', trialHoursLeft, billingEvent: 'trial_ended' }
      );
    } else if (trialStatus === 'ending_today' && trialHoursLeft && trialHoursLeft <= 24) {
      createBillingNotification(
        'billing_trial_ending_soon',
        'Trial Ending Soon',
        `Your trial ends in ${trialHoursLeft}h. Billing will begin automatically using your saved payment method.`,
        { severity: 'low', trialHoursLeft, billingEvent: 'trial_ending_soon' }
      );
    } else if (trialStatus === 'ending_soon' && trialHoursLeft && trialHoursLeft <= 72) {
      const daysLeft = Math.floor(trialHoursLeft / 24);
      console.log('[BillingNotifications] Creating ending_soon notification', {
        trialStatus,
        trialHoursLeft,
        daysLeft,
      });
      createBillingNotification(
        'billing_trial_ending_soon',
        'Trial Ending Soon',
        `Your trial ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}. Make sure your payment method is ready for automatic billing.`,
        { severity: 'low', trialHoursLeft, billingEvent: 'trial_ending_soon' }
      );
    }
  }, [
    isTenantAdmin,
    tenantId,
    user?.id,
    locked,
    graceActive,
    graceUntil,
    trialStatus,
    trialHoursLeft,
  ]);

  // This component doesn't render anything
  return null;
};

export default BillingNotifications;
